import { useState, useEffect, useMemo } from 'react';
import { Sort } from 'src/icons/Sort.icon';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, Menu, Select, Button } from 'antd';
import {
  ActionMenuIcon,
  BinIcon,
  EditAppointmentIcon,
  PlusIcon,
} from 'src/icons';
import { SuperAdminTable } from '../../../baseComponents/Table';
import {
  listAgentsAction,
  getAgentAction,
} from '../../../../store/actions/agents.action';
import AddNewAgent from './AddNewAgent';
import AddExistingAgent from './AddExistingAgent';
import EditAgent from './EditAgent';
import DeleteAgent from './DeleteAgent';
import OrganizationFilter from '../OrganizationFilter';
import { getFromStorage } from 'src/lib/storage';
import organizationEventEmitter from 'src/utilities/organizationEmmiter';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';

export const AgentsPage = () => {
  const { agentsList, agentsLoading } = useSelector(
    (state) => state?.agentsState
  );
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const { getActiveOrganizationsList } = useSelector(
    (state) => state?.organizationState
  );
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [existingAgentModalOpen, setExistingAgentModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [editingAgentId, setEditingAgentId] = useState(null);
  const [deletingAgentId, setDeletingAgentId] = useState(null);

  // Organization filtering state
  const [selectedOrganizationUUID, setSelectedOrganizationUUID] = useState(
    () => {
      const savedOrg = getFromStorage('superAdminSelectedOrganization');
      const parsedSavedOrg = savedOrg ? JSON.parse(savedOrg) : null;
      return parsedSavedOrg?.org_uuid || currentPractitioner?.org_uuid || null;
    }
  );

  const selectedOrgName = useMemo(() => {
    if (!selectedOrganizationUUID || !getActiveOrganizationsList?.length)
      return '';
    const org = getActiveOrganizationsList.find(
      (o) => o.org_uuid === selectedOrganizationUUID
    );
    return org?.org_name || '';
  }, [selectedOrganizationUUID, getActiveOrganizationsList]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedOrganizationUUID) {
      dispatch(listAgentsAction(selectedOrganizationUUID));
      dispatch(getAllActiveOrganizationAction());
    }
  }, [dispatch, selectedOrganizationUUID]);

  // Listen for organization changes
  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      (newOrg) => {
        setSelectedOrganizationUUID(newOrg?.org_uuid);
      }
    );
    return () => unsubscribe();
  }, []);

  const handleEditAgent = (agentId) => {
    setEditingAgentId(agentId);
    setEditModalOpen(true);
  };

  const handleDeleteAgent = (agentId) => {
    setDeletingAgentId(agentId);
    setDeleteModalOpen(true);
  };

  const menuOptions = (id, record) => {
    return (
      <Menu
        onClick={({ key }) => {
          if (key === 'editAgent') {
            handleEditAgent(record?.agent_id);
          }

          if (key === 'deleteAgent') {
            handleDeleteAgent(record?.agent_id);
          }
        }}
      >
        <Menu.Item key="editAgent">
          <div className="dropdown-text flex items-center space-x-2">
            <EditAppointmentIcon /> <span>Edit agent</span>
          </div>
        </Menu.Item>
        <Menu.Item key="deleteAgent">
          <div className="dropdown-text flex items-center space-x-2">
            <BinIcon /> <span>Remove agent</span>
          </div>
        </Menu.Item>
      </Menu>
    );
  };

  const nameSorter = (a, b) => {
    const nameA = a.name || '';
    const nameB = b.name || '';
    return nameA.localeCompare(nameB);
  };

  const statusSorter = (a, b) => {
    const statusA = a.status || '';
    const statusB = b.status || '';
    return statusA.localeCompare(statusB);
  };

  const columns = [
    {
      title: (
        <span className="font-[700]">
          {' '}
          Agent Name{' '}
          {agentsList?.agents?.length > 0 && (
            <Sort className={'sort-icon-doctor'} />
          )}
        </span>
      ),
      dataIndex: 'name',
      key: 'name',
      sorter: agentsList?.agents?.length > 0 ? nameSorter : false,
    },
    {
      title: <span className="font-[700]"> Phone Number </span>,
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (text) => <span>{text || 'N/A'}</span>,
    },
    {
      title: <span className="font-[700]"> External ID </span>,
      dataIndex: 'external_id',
      key: 'external_id',
      render: (text) => (
        <div className="max-w-xs truncate" title={text}>
          {text || 'N/A'}
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      dataIndex: 'actions',
      render: (text, record) => {
        return (
          <Dropdown
            className=""
            overlay={menuOptions(record?.agent_id, record)}
          >
            <a
              className=""
              onClick={(e) => {
                e.preventDefault();
              }}
            >
              <ActionMenuIcon />
            </a>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="flex justify-between items-end">
        <div className="flex items-center gap-2">
          <label className="text-md text-gray-600 font-medium">
            Select organization:
          </label>
          <OrganizationFilter shouldShowSuperAdmin={true} />
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="primary"
            onClick={() => setAgentModalOpen(true)}
            disabled={!selectedOrganizationUUID}
            className="flex items-center gap-2"
          >
            <PlusIcon />
            Add New Agent
          </Button>
          <Button
            onClick={() => setExistingAgentModalOpen(true)}
            disabled={!selectedOrganizationUUID}
            className="flex items-center gap-2"
          >
            <PlusIcon fill="#333" />
            Add Existing Agent
          </Button>
        </div>
      </div>
      <div className="mt-5 ant-table rounded-xl">
        <SuperAdminTable
          className={'appointment-page-table'}
          columns={columns}
          data={agentsList?.agents}
          loading={agentsLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} agents`,
          }}
        />
      </div>

      <AddNewAgent
        isOpen={agentModalOpen}
        setIsOpen={setAgentModalOpen}
        orgUuid={selectedOrganizationUUID}
        orgName={selectedOrgName}
      />

      <AddExistingAgent
        isOpen={existingAgentModalOpen}
        setIsOpen={setExistingAgentModalOpen}
        orgUuid={selectedOrganizationUUID}
        orgName={selectedOrgName}
      />

      <EditAgent
        isOpen={editModalOpen}
        setIsOpen={setEditModalOpen}
        agentId={editingAgentId}
        orgUuid={selectedOrganizationUUID}
      />

      <DeleteAgent
        isOpen={deleteModalOpen}
        setIsOpen={setDeleteModalOpen}
        agentId={deletingAgentId}
        orgUuid={selectedOrganizationUUID}
      />
    </div>
  );
};

export default AgentsPage;
